import {
	Box,
	Col,
	Container,
	Divider,
	Grid,
	Heading,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui/base"
import { Breadcrumbs, ButtonLink, Link } from "@sembark-travel/ui/router"
import { Helmet } from "react-helmet-async"
import { generatePath } from "../../../router-utils"
import { PlatformIcon, type TIntegration } from "../../../Integrations"
import { useHasFeatureFlag } from "../../../Auth"

export default function NewIntegrationPage() {
	const canIntegrateFB = useHasFeatureFlag("fb_app_integration")
	const canIntegratePG = useHasFeatureFlag("payment_gateway")
	const canIntegratePGPayU = useHasFeatureFlag("payment_gateway_payu")
	const canIntegratePGSimulator = useHasFeatureFlag("payment_gateway_simulator")
	return (
		<>
			<Helmet>
				<title>New Integration</title>
			</Helmet>
			<Breadcrumbs
				title="New Integration"
				items={[
					[generatePath("/org/integrations"), "Integrations"],
					["", "New"],
				]}
			/>
			<Box paddingX={{ md: "4" }} paddingY={{ md: "8" }}>
				<Box maxWidth="3xl" marginX="auto">
					<Box borderWidth={{ md: "1" }} rounded="md" bgColor="default">
						<Container paddingY="6">
							<Stack gap="4">
								<Stack>
									<Heading as="h2">Lead Platform Integrations</Heading>
									<Text color="muted">
										Use the official integrations to bring leads from following
										Ads platforms.
									</Text>
								</Stack>
								<PlatformIntegations
									integrations={[
										{
											url: generatePath("/integrations/google-ads/new"),
											platform: "google_ads" as TIntegration["platform"],
											title: "Google Ads Lead Form",
											description: "Integrate Lead Form on Ads",
										},
									].concat(
										canIntegrateFB
											? [
													{
														url: generatePath("/integrations/meta/new"),
														platform: "meta" as const,
														title: "Meta (FB/Insta) Page Leads",
														description: "Bring leads from FB/Insta Pages",
													},
												]
											: []
									)}
								/>
							</Stack>
							<Divider />
							<Stack gap="4">
								<Stack>
									<Heading as="h2">Custom API Integrations</Heading>
									<Text color="muted">
										Use the APIs to integrate with any platform such as Website,
										Landing Pages, ChatBots etc.
									</Text>
								</Stack>
								<PlatformIntegations
									integrations={[
										{
											url: generatePath("/integrations/api/new"),
											platform: "api",
											title: "API Integration",
											description:
												"Integrate with Landing Page, ChatBots using API and Tokens",
										},
									]}
								/>
							</Stack>
							{canIntegratePG ? (
								<>
									<Divider />
									<Stack gap="4">
										<Stack>
											<Heading as="h2">Payment Solutions</Heading>
											<Text color="muted">
												Collect and Manage Payments by directly integrating your
												Payment Gateway Accounts.
											</Text>
										</Stack>
										<PlatformIntegations
											integrations={([] as Array<TPlatformIntegrationItem>)
												.concat(
													canIntegratePGPayU
														? [
																{
																	url: generatePath("/integrations/payu/new"),
																	platform: "pg_payu" as const,
																	title: "PayU Payment Gateway",
																	description: "Collect and Manage Payments",
																},
															]
														: []
												)
												.concat(
													canIntegratePGSimulator
														? [
																{
																	url: generatePath(
																		"/integrations/pg-simulator/new"
																	),
																	platform: "pg_simulator" as const,
																	title: "Payment Gateway Simulator",
																	description:
																		"Collect and Manage Payments using Gateway Simulator",
																},
															]
														: []
												)}
										/>
									</Stack>
								</>
							) : null}
							<Divider />
							<ButtonLink to={generatePath("/org/integrations")} replace>
								Cancel
							</ButtonLink>
						</Container>
					</Box>
				</Box>
			</Box>
		</>
	)
}

type TPlatformIntegrationItem = {
	url: string
	platform: TIntegration["platform"]
	title: string
	description: string
}

function PlatformIntegations({
	integrations,
}: {
	integrations: Array<TPlatformIntegrationItem>
}) {
	return (
		<Grid gap="4">
			{integrations.map(({ url, platform, title, description }, index) => (
				<Col
					key={platform}
					xs={12}
					sm={index % 2 === 0 && integrations.length > 1 ? 6 : true}
				>
					<Link
						height="full"
						to={url}
						padding="4"
						borderWidth="1"
						rounded="md"
						display="block"
						backgroundColor={{ hover: "subtle" }}
					>
						<Inline gap="4" alignItems="center">
							<Box>
								<PlatformIcon size="48" platform={platform} />
							</Box>
							<Stack flex="1">
								<Heading as="h3" fontSize="md">
									{title}
								</Heading>
								<Text color="muted">{description}</Text>
							</Stack>
						</Inline>
					</Link>
				</Col>
			))}
		</Grid>
	)
}
